import React from "react";
import BaseModal from "./BaseModal";
import { formatCurrency } from "@/utils/formatters";

/**
 * CashByAccountModal - Shows the per-account breakdown behind the "Cash Balance"
 * stat tile on the All Accounts summary page.
 *
 * @param {Object} props
 * @param {Array<{account_id: string, account_name: string, cash_balance: number}>} props.accounts
 * @param {number} props.total - Aggregate cash balance (should equal the sum of accounts)
 * @param {Function} props.onClose
 */
const CashByAccountModal = ({ accounts = [], total = 0, onClose }) => {
  const sortedAccounts = [...accounts].sort(
    (a, b) => (b.cash_balance || 0) - (a.cash_balance || 0),
  );

  const header = (
    <div className="flex items-center justify-between p-6">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white">
        Cash by Account
      </h2>
      <button
        onClick={onClose}
        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );

  const footer = (
    <div className="flex justify-end p-6">
      <button
        onClick={onClose}
        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium transition-colors"
      >
        Close
      </button>
    </div>
  );

  return (
    <BaseModal header={header} footer={footer} onClose={onClose} maxWidth="max-w-md">
      <div className="p-6">
        {sortedAccounts.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
            No cash held.
          </p>
        ) : (
          <div className="space-y-3">
            {sortedAccounts.map((acct) => (
              <div
                key={acct.account_id}
                className="flex items-center justify-between"
              >
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {acct.account_name}
                </span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(acct.cash_balance)}
                </span>
              </div>
            ))}

            <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
              <span className="text-sm font-bold text-gray-900 dark:text-white">
                Total
              </span>
              <span className="text-sm font-bold text-gray-900 dark:text-white">
                {formatCurrency(total)}
              </span>
            </div>
          </div>
        )}
      </div>
    </BaseModal>
  );
};

export default CashByAccountModal;
